import { Box, Link, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { StoryPageLayout } from '../layouts';

const PAGE_URL = 'https://www.rsmc.ai/solution-guides/hublife-for-nonprofits';
const PAGE_IMAGE = 'https://www.rsmc.ai/images/nonprofit-volunteers-hero.jpg';
const PAGE_DESCRIPTION =
  'Most nonprofit generative AI projects stall after proof of concept. Here is why the problem is data, not technology, and how HubLife unifies donor, financial, and operational systems to deliver agentic AI in weeks, not months.';

// Common paragraph styling
const paragraphSx = {
  fontSize: '1rem',
  lineHeight: 1.8,
  color: 'var(--color-text)',
  mb: 3,
};

// Section heading styling
const headingSx = {
  fontWeight: 700,
  mb: 3,
  mt: 6,
  color: 'var(--color-heading)',
  fontSize: { xs: '1.25rem', md: '1.5rem' },
};

// Link styling
const linkSx = {
  color: '#5c6bc0',
  textDecoration: 'underline',
  '&:hover': {
    color: '#3949ab',
  },
};

// Body copy inside the bordered panels
const panelBodySx = {
  fontSize: '0.9375rem',
  lineHeight: 1.7,
  color: 'var(--color-text-muted)',
};

/**
 * Bordered panel with the RSMC green left rule. Used for the data-problem
 * callout, the differentiator cards, and the "Why RSMC" cards.
 *
 * @param {Object} props - Component props
 * @param {string} props.heading - Panel heading
 * @param {import('react').ReactNode} props.children - Panel body
 * @returns {import('react').ReactElement} A bordered panel
 */
function Panel({ heading, children }) {
  return (
    <Box
      sx={{
        borderLeft: '4px solid var(--color-primary)',
        backgroundColor:
          'color-mix(in srgb, var(--color-primary) 6%, transparent)',
        borderRadius: '0 12px 12px 0',
        p: { xs: 2.5, md: 3 },
        mb: 2.5,
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          fontSize: '1.0625rem',
          color: 'var(--color-heading)',
          mb: 1.5,
        }}
      >
        {heading}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * Centred pull quote with an optional attribution line.
 *
 * @param {Object} props - Component props
 * @param {string} props.quote - The quoted text
 * @param {string} [props.cite] - Attribution shown beneath the quote
 * @returns {import('react').ReactElement} A pull quote
 */
function PullQuote({ quote, cite }) {
  return (
    <Box component="figure" sx={{ my: 6, mx: 0, textAlign: 'center' }}>
      <Typography
        component="blockquote"
        sx={{
          fontSize: { xs: '1.25rem', md: '1.5rem' },
          lineHeight: 1.4,
          fontWeight: 600,
          color: 'var(--color-heading)',
          fontStyle: 'italic',
          m: 0,
        }}
      >
        &ldquo;{quote}&rdquo;
      </Typography>
      {cite && (
        <Typography
          component="figcaption"
          sx={{
            mt: 2,
            fontSize: '0.875rem',
            color: 'var(--color-primary)',
            fontWeight: 600,
            letterSpacing: '0.5px',
          }}
        >
          {cite}
        </Typography>
      )}
    </Box>
  );
}

const dataProblems = [
  'Manual reporting consumes 40+ hours a month across finance and program teams',
  'Grant governance and donor management cannot scale',
  'Impact reporting for boards and funders is incomplete or inaccurate',
  'Data silos block a holistic view of organizational performance',
];

const differentiators = [
  {
    heading: 'Your Data. Your Agents. Your Platform.™',
    body: 'Runs entirely inside your own AWS account — no vendor co-tenancy, no model training on your information, no third-party data exposure.',
  },
  {
    heading: 'Enterprise-grade in every layer',
    body: 'AWS infrastructure, foundation models, advanced analytics, agentic framework, and managed operations built to the same standard that powers Fortune 500 AI workloads — tuned for mission outcomes.',
  },
  {
    heading: 'Security & compliance built-in',
    body: "Inherits the AWS security framework that protects the world's most regulated workloads: role-based access, IAM and Active Directory single sign-on, encryption in transit and at rest, and audit-ready compliance.",
  },
  {
    heading: 'Fully managed',
    body: 'RSMC handles AWS account setup and operations for organizations new to AWS, AgenticOps and Data Specialists guiding safe AI adoption, and FinOps governance for predictable cloud and AI spend.',
  },
  {
    heading: 'Built to grow',
    body: 'Your AWS environment scales as your mission scales. Layer in new agents, connect new data sources, and activate additional AWS-native services as use cases emerge — consumption-based pricing means you only pay for what you turn on.',
  },
];

const capabilities = [
  {
    heading: 'Shared knowledge foundation',
    body: "Bring your team's files, data, and context into collaborative workspaces.",
  },
  {
    heading: 'Build custom AI agents',
    body: "Create agents tailored to your team's specific domain and data.",
  },
  {
    heading: 'Conduct deep research',
    body: 'Generate multi-source, synthesized analysis grounded in your business data.',
  },
  {
    heading: 'Visualize your data',
    body: 'Go from question to interactive dashboard without a separate BI tool.',
  },
  {
    heading: 'Simple automations',
    body: 'Connect your tools, define tasks, set a trigger, and let Quick handle the rest.',
  },
  {
    heading: 'Full orchestration',
    body: 'Multi-step workflows that span systems, apply logic, and run autonomously.',
  },
  {
    heading: 'Build custom apps',
    body: 'Create interactive tools for your team without writing code.',
  },
  {
    heading: 'Create deliverables',
    body: "Generate documents, presentations, spreadsheets, and visuals — and refine them until they're exactly right.",
  },
];

const awsServices = [
  'Amazon Quick',
  'Amazon Bedrock',
  'Amazon S3',
  'AWS Lambda',
  'AWS CloudTrail',
  'Amazon CloudWatch',
  'AWS Glue',
  'Amazon Athena',
  'Amazon EventBridge',
  'AWS API MCP',
];

const timeline = [
  {
    label: 'Week 1',
    heading: 'Discovery sprint',
    body: 'Opens with a focused sprint on one use case: Impact Reporting, Grant Management, or Finance Dashboards.',
  },
  {
    label: 'Week 2',
    heading: 'Context activation',
    body: 'Activates the context phase with standard agents and spaces built on your unified data foundation.',
  },
  {
    label: 'Ongoing',
    heading: 'Managed adoption roadmap',
    body: 'Our managed AgenticOps and Data Specialist team delivers a roadmap for organization-wide adoption that keeps every deployment compliant, secure, and aligned to mission outcomes.',
  },
];

const whyRsmc = [
  {
    heading: 'Ready on Day One',
    body: 'Service-led and human-first. We layer your context onto AWS fully managed services and deliver measurable value on day one.',
  },
  {
    heading: 'Secure by Design',
    body: 'AWS Well-Architected at the core; auditable identity and access management, data governance, AI guardrails, observability and traceability for everything.',
  },
  {
    heading: 'Managed by Experts',
    body: 'Certified AWS engineers, software architects, FinOps practitioners, and data specialists operating every solution we deliver.',
  },
  {
    heading: 'Cloud Economics',
    body: "We design AWS solutions around your specific drivers: research velocity, cost per beneficiary, donor experience, or whatever matters most to your mission's success.",
  },
];

/**
 * HubLife for Nonprofits Solution Guide
 *
 * Long-form solution guide covering why nonprofit agentic AI projects stall on
 * fragmented data, and how HubLife unifies the data foundation first. Uses
 * StoryPageLayout so the AWS-funded POC form sits in a sticky sidebar beside
 * the content.
 *
 * @returns {import('react').ReactElement} HubLife for Nonprofits solution guide page
 */
function HubLifeNonprofitsSolutionGuide() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline:
      'HubLife for Nonprofits: Fixing the Data Crisis Before You Bolt On AI',
    description: PAGE_DESCRIPTION,
    author: {
      '@type': 'Organization',
      name: 'Ready Secure Managed Cloud',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Ready Secure Managed Cloud',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.rsmc.ai/images/r-logo.png',
      },
    },
    image: PAGE_IMAGE,
    datePublished: '2026-08-18',
    dateModified: '2026-08-18',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': PAGE_URL,
    },
  };

  return (
    <>
      <Helmet>
        <title>
          HubLife&trade; for Nonprofits: Why Agentic AI Fails Without a Data
          Foundation | RSMC
        </title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          property="og:title"
          content="HubLife for Nonprofits: Helping Mission-Driven Organizations Become AI Ready."
        />
        <meta
          property="og:description"
          content="RSMC's take on fixing the nonprofit data crisis before layering on AI."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={PAGE_IMAGE} />
        <meta
          name="twitter:title"
          content="HubLife for Nonprofits: How Nonprofits can Own their AI Journey"
        />
        <meta
          name="twitter:description"
          content="Why nonprofit AI projects stall on fragmented data, and how HubLife delivers agentic AI in weeks, not months."
        />
        <meta
          name="keywords"
          content="HubLife, agentic AI, nonprofit technology, Amazon Quick, AWS partner, data strategy, nonprofit AI, AgenticOps"
        />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <StoryPageLayout
        title="HubLife™ for Nonprofits"
        subtitle="Helping Mission-Driven Organizations Become AI Ready."
        formHeading="HubLife POC to Production in 2 Weeks"
        formFilePath="/forms/aws-funded-poc-form.txt"
        heroImage="/images/nonprofit-volunteers-hero.jpg"
        heroImageAlt="Volunteers distributing crates of donated food outdoors for a nonprofit outreach program"
        errorMessage="Unable to load contact form"
        loadingMessage="Loading contact form..."
      >
        <Box component="article">
          {/* Article Title */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              color: 'var(--color-heading)',
              mb: 3,
            }}
          >
            How Nonprofits can Own their AI Journey
          </Typography>

          {/* Deck */}
          <Typography
            sx={{
              ...paragraphSx,
              fontSize: '1.125rem',
              color: 'var(--color-text-muted)',
              mb: 4,
            }}
          >
            Mission-driven organizations have accumulated a wealth of data, but
            most nonprofits are running on a patchwork of disconnected systems
            that quietly sabotage every AI initiative before it reaches impact.
          </Typography>

          <Typography sx={paragraphSx}>
            Donor CRMs, financial systems, and spreadsheets scatter critical
            information across the organization. Layering AI on top of
            disconnected systems wastes budget, exhausts teams, and fails to
            deliver mission impact.
          </Typography>

          {/* The hidden cost of fragmented data */}
          <Typography variant="h2" sx={headingSx}>
            The hidden cost of fragmented data
          </Typography>

          <Typography sx={paragraphSx}>
            By the end of 2025, Gartner reported
            <sup>1</sup> that organizations abandoned at least 50% of generative
            AI projects after proof of concept. Poor data quality, inadequate
            controls, and unclear business value derailed them. The technology
            isn't the problem. The data is.
          </Typography>

          <Typography sx={paragraphSx}>
            Nonprofits running on a patchwork of SaaS systems watch AI
            initiatives stall before they reach impact. Budgets drain, staff
            burn out, and funder goodwill erodes.
          </Typography>

          <Panel heading="The data problem shows up everywhere">
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {dataProblems.map((item) => (
                <Typography
                  component="li"
                  key={item}
                  sx={{
                    fontSize: '0.9375rem',
                    lineHeight: 1.7,
                    color: 'var(--color-text)',
                    mb: 1,
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Panel>

          <Typography sx={{ ...paragraphSx, mt: 3 }}>
            HubLife&trade; is purpose-built for the data reality nonprofits live
            in every day. Rather than layer AI onto fragmented systems,
            HubLife&trade; unifies the data foundation first, with pre-built
            integrations across the donor, financial, and operational systems
            nonprofits already run.
          </Typography>

          <Typography sx={paragraphSx}>
            Mission impact follows. Pre-configured agents, dashboards, and
            automation deliver operational AI in weeks, not months. RSMC's
            AgenticOps and Data Specialists run HubLife&trade; end to end,
            making your data accurate, usable, and ready for AI, and bringing
            the specialized expertise most nonprofits don't have in-house. They
            keep your AI trusted, governed, secure, and aligned to mission
            outcomes long after launch.
          </Typography>

          {/* Agentic AI as a service */}
          <Typography variant="h2" sx={headingSx}>
            Agentic AI as a service, not another chatbot
          </Typography>

          <Typography sx={paragraphSx}>
            Most AI tools answer questions. Agentic AI does the work. It pulls
            together your data, learns about what it finds, and carries out a
            task, such as drafting a report, flagging an anomaly, or building a
            schedule, with a person reviewing the result.
          </Typography>

          <Typography sx={paragraphSx}>
            HubLife&trade; delivers this as a{' '}
            <strong>
              fully managed, nonprofit-specific edition of Amazon Quick
            </strong>
            . The platform comes pre-loaded with the dashboards, datasets, and
            AI agents nonprofit teams need most, and it connects to the systems
            you already use: QuickBooks, Salesforce, Microsoft OneDrive and
            SharePoint, Google Drive, Canva, Figma, Slack, Asana, and Zapier all
            integrate out of the box, alongside 100+ additional application and
            data source integrations.
          </Typography>

          <PullQuote quote="Most AI tools answer questions. Agentic AI does the work." />

          {/* How HubLife is different */}
          <Typography variant="h2" sx={headingSx}>
            How HubLife&trade; is different
          </Typography>

          <Typography sx={paragraphSx}>
            Unlike AI tools like ChatGPT or Claude, HubLife&trade; runs entirely
            inside a governed secured AWS account that you own. The data, the
            agents, and the platform belong to your organization. No vendor
            co-tenancy, no model training on your information, no third-party
            data exposure.
          </Typography>

          {differentiators.map((item) => (
            <Panel key={item.heading} heading={item.heading}>
              <Typography sx={panelBodySx}>{item.body}</Typography>
            </Panel>
          ))}

          <PullQuote
            quote="This isn't another SaaS solution. HubLife is an enterprise suite of AWS services. Built for the mission. Owned by the nonprofit."
            cite="Rochelle Manns — Founder & CIO, Ready Secure Managed Cloud"
          />

          {/* A comprehensive AI productivity suite */}
          <Typography variant="h2" sx={headingSx}>
            A comprehensive AI productivity suite
          </Typography>

          <Typography sx={paragraphSx}>
            Strategic AI adoption isn't "more LLMs everywhere." It's the right
            capability for the right job. Sometimes the answer is an LLM.
            Sometimes it's a workflow that runs itself. Sometimes it's a
            predictive model, an automation, or a natural-language query against
            your data.
          </Typography>

          <Typography sx={paragraphSx}>
            HubLife&trade; brings the full enterprise toolkit: automation,
            advanced analytics, machine learning, natural-language queries,
            agentic AI, LLMs, and broad data connectivity. Value gets unlocked
            at every stage of business process maturity, not just the moments
            suited to generative AI.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            {capabilities.map((item) => (
              <Box
                key={item.heading}
                sx={{
                  border: '1px solid var(--color-border)',
                  borderRadius: '12px',
                  backgroundColor: 'var(--color-card)',
                  p: 2.5,
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: 'var(--color-heading)',
                    mb: 1,
                  }}
                >
                  {item.heading}
                </Typography>
                <Typography sx={panelBodySx}>{item.body}</Typography>
              </Box>
            ))}
          </Box>

          <PullQuote
            quote="HubLife gave us AI powered grant research, provider automation workflows, and data visualization in one platform, enabling Rising Routes to serve outdoor access providers nationally and at enterprise scale."
            cite="Jason Swann — Founder & Executive Director, Rising Routes Alliance"
          />

          {/* What's actually running under the hood */}
          <Typography variant="h2" sx={headingSx}>
            What's actually running under the hood
          </Typography>

          <Typography sx={paragraphSx}>
            HubLife&trade; runs on a curated set of AWS services. Together they
            form an enterprise-grade AI productivity suite: every action carries
            an audit-ready record, every dataset stays private, and every
            workload runs with governance and enterprise security.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
            {awsServices.map((service) => (
              <Typography
                key={service}
                component="span"
                sx={{
                  border: '1px solid var(--color-primary)',
                  borderRadius: '999px',
                  px: 2,
                  py: 0.75,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'var(--color-heading)',
                  backgroundColor:
                    'color-mix(in srgb, var(--color-primary) 8%, transparent)',
                }}
              >
                {service}
              </Typography>
            ))}
          </Box>

          <Typography sx={paragraphSx}>
            RSMC doesn't just hand over the technology. Our AgenticOps team
            plans, builds, tests, deploys, and continuously improves your agents
            with human review, context layers, and real-time monitoring.
            Optional enhanced AI governance controls strengthen every
            deployment. Our Data Specialists support data management, reporting,
            and the roadmap that drives continued HubLife&trade; adoption.
          </Typography>

          {/* From first conversation to full deployment */}
          <Typography variant="h2" sx={headingSx}>
            From first conversation to full deployment
          </Typography>

          <Typography sx={paragraphSx}>
            HubLife&trade; runs as a structured two-week,
            proof-of-value-to-production engagement.
          </Typography>

          <Box sx={{ mb: 3 }}>
            {timeline.map((step) => (
              <Box
                key={step.label}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 3 },
                  borderTop: '1px solid var(--color-border)',
                  py: 2.5,
                }}
              >
                <Typography
                  sx={{
                    flex: '0 0 100px',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'var(--color-primary)',
                  }}
                >
                  {step.label}
                </Typography>
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: 'var(--color-heading)',
                      mb: 0.5,
                    }}
                  >
                    {step.heading}
                  </Typography>
                  <Typography sx={panelBodySx}>{step.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Why RSMC */}
          <Typography variant="h2" sx={headingSx}>
            Why RSMC
          </Typography>

          <Typography sx={paragraphSx}>
            Ready Secure Managed Cloud&trade; (RSMC) is an AWS partner helping
            mission-driven organizations and small and medium size businesses
            modernize with AWS services.
          </Typography>

          {whyRsmc.map((item) => (
            <Panel key={item.heading} heading={item.heading}>
              <Typography sx={panelBodySx}>{item.body}</Typography>
            </Panel>
          ))}

          {/* Footnotes */}
          <Box
            sx={{ mt: 6, pt: 3, borderTop: '1px solid var(--color-border)' }}
          >
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: 'var(--color-text-muted)',
                lineHeight: 1.6,
              }}
            >
              1. Gartner, Inc.{' '}
              <Link
                href="https://www.gartner.com/en/articles/genai-project-failure"
                target="_blank"
                rel="noopener noreferrer"
                sx={linkSx}
              >
                Why 50% of GenAI Projects Fail &mdash; And How to Beat the Odds
              </Link>
            </Typography>
          </Box>

          {/* Visual separator - R logo */}
          <Box sx={{ textAlign: 'center', mt: 6, mb: 2 }}>
            <Box
              component="img"
              src="/images/r-logo.png"
              alt="Ready Secure Managed Cloud"
              sx={{ width: '50px', height: 'auto', opacity: 0.7 }}
            />
          </Box>
        </Box>
      </StoryPageLayout>
    </>
  );
}

export default HubLifeNonprofitsSolutionGuide;
